import { useGet } from 'hooks/use-get';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import { getScenarioStatus } from 'views/bluviu-nutrition/api/get-scenario-status';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';

type ScenarioStatusPollingItem = {
  id: string;
  globalSiteId: string;
  state: ScenarioState | string;
  model: ScenarioModel;
};

const listQueryKeyMap: Record<ScenarioModel, string> = {
  [ScenarioModel.EFFECTIVE_ENERGY_GT_2]: 'getScenarios',
  [ScenarioModel.EFFECTIVE_ENERGY_GT_2_MULTI]: 'getR2Scenarios',
};

export const useScenarioStatusPolling = (scenario: ScenarioStatusPollingItem) => {
  const queryClient = useQueryClient();

  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const [updatedScenarioStatus, setUpdatedScenarioStatus] = useState<boolean>(false);
  const previousScenarioRunning = useRef<boolean | undefined>();

  const listQueryKey = listQueryKeyMap[scenario.model];

  const scenarioStatusQuery = useGet(
    ['getScenarioStatus', scenario.globalSiteId, scenario.id],
    ({ queryKey }) => getScenarioStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: scenario.state === ScenarioState.SUBMITTED,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    const scenarioStatus = scenarioStatusQuery.data;
    if (!scenarioStatus) return;

    if (previousScenarioRunning.current === true && !scenarioStatus.scenarioIsRunning) {
      queryClient.invalidateQueries({
        queryKey: [listQueryKey, scenario.globalSiteId],
        exact: true,
      });
      setUpdatedScenarioStatus(true);
    }
    previousScenarioRunning.current = scenarioStatus.scenarioIsRunning;
    setRefetchInterval(scenarioStatus.scenarioIsRunning ? 3000 : false);
  }, [listQueryKey, queryClient, scenario.globalSiteId, scenarioStatusQuery.data]);

  const completedStates = [ScenarioState.COMPLETED, ScenarioState.SCENARIO_RESULT_SENT];
  const isComplete = updatedScenarioStatus && completedStates.includes(scenario.state as ScenarioState);
  const isRunning = !isComplete && scenario.state === ScenarioState.SUBMITTED && !!scenarioStatusQuery.data?.scenarioIsRunning;

  return { isComplete, isRunning };
};
